import React, {useEffect, useRef, useState, useContext} from "react";
import {LettersContext, LettersDispatch} from "../context/LettersContext";
import {
    convertCmToPxWithScaling,
    convertMMtoPxWithScaling,
    calculateActualSize,
    convertCmToPx, convertMMtoPx,
    pickMeasurementChar
} from "../services/helpers";
import {WorkingSizeContext} from "../context/WorkingSizeContext";
import {TextSectionsDispatch, TextSectionsListContext} from "../context/TextSectionsListContext";
import FontsMapper from "../services/FontsMapper";
import {useLanguage} from "../context/LanguageContext";

const MIN_MARGIN = -20;
const MAX_MARGIN = 20;

const TextSectionLetters = ({textSection, index, configContext}) => {
    const {t} = useLanguage();
    const lettersMap = useContext(LettersContext);
    const lettersDispatch = useContext(LettersDispatch);
    const lettersArray = lettersMap[index];
    const workingSizeContext = useContext(WorkingSizeContext);
    const containerRef = useRef(null);
    const [currentMargin, setCurrentMargin] = useState(0);
    const [inputDisabled, setInputDisabled] = useState(true);
    const [activeLetterIndex, setActiveLetterIndex] = useState(null);
    const [previewMargin, setPreviewMargin] = useState(null);
    const textSectionDispatch = useContext(TextSectionsDispatch);

    const fontsMapper = new FontsMapper();

    // Funkcja do aktualizacji margin-right tylko w HTML (podgląd)
    const updatePreviewMargin = (margin) => {
        if (activeLetterIndex === null) return;
        const container = containerRef.current;
        if (!container) return;
        const letters = container.querySelectorAll('.single-letter');
        if (letters[activeLetterIndex]) {
            letters[activeLetterIndex].style.marginRight = `${margin}px`;
        }
    };

    // Resetuj margin podglądu do wartości z lettersArray lub 0
    const resetPreviewMargin = () => {
        if (activeLetterIndex === null) return;
        const container = containerRef.current;
        if (!container) return;
        const letters = container.querySelectorAll('.single-letter');
        if (letters[activeLetterIndex]) {
            const margin = (lettersArray?.[activeLetterIndex]?.margin ?? 0);
            letters[activeLetterIndex].style.marginRight = `${margin}px`;
        }
    };

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        function handleClick(e) {
            if (e.target.classList.contains('single-letter')) {
                // Usuń klasę active ze wszystkich liter
                container.querySelectorAll('.single-letter.active').forEach(el => {
                    el.classList.remove('active');
                });
                // Dodaj klasę active do klikniętej litery
                e.target.classList.add('active');
                // Pobierz index klikniętej litery
                const letters = Array.from(container.querySelectorAll('.single-letter'));
                const idx = letters.indexOf(e.target);
                setActiveLetterIndex(idx);
                // Pobierz margin-right
                const style = window.getComputedStyle(e.target);
                const marginRight = parseInt(style.marginRight, 10) || 0;
                setCurrentMargin(marginRight);
                setPreviewMargin(marginRight);
                setInputDisabled(false);
            } else if (e.target.classList.contains('input-change-margin')) {
                // Pozostaw aktywność, nie rób nic
            } else {
                // Kliknięcie poza literą i inputem - dezaktywuj wszystko
                container.querySelectorAll('.single-letter.active').forEach(el => {
                    el.classList.remove('active');
                });
                setInputDisabled(true);
                setActiveLetterIndex(null);
                setCurrentMargin(0);
                setPreviewMargin(null);
            }
        }

        function handleDocumentClick(e) {
            if (
                containerRef.current &&
                !containerRef.current.contains(e.target) &&
                !e.target.classList.contains('input-change-margin')
            ) {
                containerRef.current.querySelectorAll('.single-letter.active').forEach(el => {
                    el.classList.remove('active');
                });
                setInputDisabled(true);
                setActiveLetterIndex(null);
                setCurrentMargin(0);
                setPreviewMargin(null);
            }
        }

        container.addEventListener('click', handleClick);
        document.addEventListener('mousedown', handleDocumentClick);

        return () => {
            container.removeEventListener('click', handleClick);
            document.removeEventListener('mousedown', handleDocumentClick);
        };
    }, [lettersArray]);

    // Podgląd zmiany marginesu na bieżąco
    useEffect(() => {
        if (inputDisabled) {
            resetPreviewMargin();
        } else if (previewMargin !== null) {
            updatePreviewMargin(previewMargin);
        }
    }, [previewMargin, inputDisabled, activeLetterIndex]);

    // Obsługa zmiany wartości inputa (number lub range)
    const handleMarginChange = (value) => {
        const clampedValue = Math.max(MIN_MARGIN, Math.min(MAX_MARGIN, value));
        setCurrentMargin(clampedValue);
        setPreviewMargin(clampedValue);

        if (activeLetterIndex === null || !lettersArray || !lettersArray[activeLetterIndex]) {
            return;
        }

        const updatedLetters = [...lettersArray];
        const newLetterHtml = `<p class="single-letter" style="margin-right:${clampedValue}px">${updatedLetters[activeLetterIndex].letter}</p>`;
        updatedLetters[activeLetterIndex] = {
            ...updatedLetters[activeLetterIndex],
            margin: clampedValue,
            html: newLetterHtml,
        };

        lettersDispatch({
            type: 'updateLetterMargin',
            payload: {
                sectionIndex: index,
                letterIndex: activeLetterIndex,
                margin: clampedValue,
                html: newLetterHtml,
            }
        });

        let html = '';
        updatedLetters.forEach((letter) => {
            html += letter.html;
        });

        updateTextSectionData(html);
    };

    const updateTextSectionData = (html) => {
        return new Promise((resolve) => {
            textSectionDispatch({
                type: "updateTextSectionData",
                payload: {
                    index,
                    value: html,
                    rawContent: textSection[index]?.rawContent,
                }
            });
            resolve();
        });
    }

    const handleInputChange = (e) => {
        const value = parseInt(e.target.value, 10) || 0;
        handleMarginChange(value);
    };

    const handleRangeChange = (e) => {
        const value = parseInt(e.target.value, 10) || 0;
        handleMarginChange(value);
    };

    // Looks up the per-size allowed-character flags for the current font.
    // Needed because each text section can use a different size, and subset
    // font files (e.g. numbers-only) require measuring a glyph they actually contain.
    const getMeasurementCharForSize = (fontSizeValue) => {
        const sizeConf = configContext.fontsSizesList?.find(
            (f) => f.font_size?.slug === String(fontSizeValue)
        );
        return pickMeasurementChar({
            allowedBigLetters: sizeConf?.allowed_big_letters,
            allowedSmallLetters: sizeConf?.allowed_small_letters,
            allowedNumbers: sizeConf?.allowed_numbers,
            allowedSpecial: sizeConf?.allowed_special,
        });
    }

    const actualFontSize = (textSection, workingSizeContext , fontsMapper ) => {
        const fontSizeValue = textSection[index].fontSizeValue;
        return calculateActualSize(
            convertMMtoPx(fontSizeValue),
            workingSizeContext.scale,
            fontsMapper.fonts[configContext.woocommerceProductId].normal[fontSizeValue].name,
            getMeasurementCharForSize(fontSizeValue)
        )
    }

    const marginTop = (textSection, workingSizeContext , fontsMapper) => {
        const fontSizeValue = textSection[index].fontSizeValue;
        const fontName = fontsMapper.fonts[configContext.woocommerceProductId].normal[fontSizeValue].name;
        const actualSize = calculateActualSize(
            convertMMtoPx(fontSizeValue),
            workingSizeContext.scale,
            fontName,
            getMeasurementCharForSize(fontSizeValue)
        )
        // Dla fontu Corsiva używamy descent2, dla pozostałych descent
        // problem z tym fontem polega na tym, że jego metryki są niedokładne i descent jest zbyt mały,
         if (fontName.toLowerCase().includes('corsiva')) {
             return actualSize.descent2
         } else {
            return actualSize.descent
         }
    }

    return (
        <>
            <div
                id={`single-letter-container-${index}`}
                style={{
                    display: 'inline-flex' ,
                    alignItems: 'flex-start',
                }}
                ref={containerRef}
                className={`single-letter-container`}
                dangerouslySetInnerHTML={{__html: `
                    <style>
                        #single-letter-container-${index} p.single-letter {
                            display: flex;
                            align-items: center;
                            margin-right: ${currentMargin}px;
                            margin-top: ${marginTop(textSection,workingSizeContext , fontsMapper )}px;
                            font-size: ${ actualFontSize(textSection,workingSizeContext , fontsMapper ).actualSize}px;
                            font-family: "${fontsMapper.fonts[configContext.woocommerceProductId].normal[textSection[index].fontSizeValue].name}";
                            letter-spacing: ${convertCmToPxWithScaling(textSection[index].letterSpacing, workingSizeContext.scale) - ( 16 * workingSizeContext.scale ) }px;
                            text-align: ${textSection[index].textAlign};
                            line-height: 1;
                        }
                    </style>
                    ${textSection[index].value}
                `}}
            ></div>
            {/*Here we edit letter margins */}
            <div className="select-margin select-margin--first" style={{
                gap: '5px',
            }}>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                }}>
                    <div style={{
                        display: 'flex',
                        gap: '10px',
                        alignItems: 'center',
                    }}
                    >
                        <input
                            className="input-change-margin"
                            type="range"
                            id="changeCurrentLetterMarginRange"
                            min={MIN_MARGIN}
                            max={MAX_MARGIN}
                            value={currentMargin}
                            disabled={inputDisabled}
                            onChange={handleRangeChange}
                            style={{width: '100px'}}
                        />
                        <input
                            style={{
                                width: '40px',
                                height: '25px',
                                padding: '1px',
                                fontSize: '12px',
                                fontFamily: 'Montserrat',
                                textAlign: 'center',
                            }}
                            className="input-change-margin"
                            id="changeCurrentLetterMargin"
                            type="number"
                            value={currentMargin}
                            min={MIN_MARGIN}
                            max={MAX_MARGIN}
                            disabled={inputDisabled}
                            onChange={handleInputChange}
                        />
                    </div>
                    <span style={{
                        fontSize: '10px',
                        fontFamily: "Montserrat",
                        textAlign: 'left',
                    }}>*{t('textSection.clickLetterHint')} <br/> {t('textSection.editSpacingHint')}</span>
                </div>
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                }}>
                </div>
            </div>
        </>
    );
};

export default TextSectionLetters;
